import React, { useEffect, useRef, useState } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";
import { normalizePreviewLabel } from "./previewLabel";
import { numericMathExpression, previewMathExpression } from "./mathExpression";

const INDENT = 36;
const PANEL_WIDTH_DESKTOP = 250;

function pickString(value) {
  if (value && typeof value === "object" && "raw" in value) {
    return value.raw ?? "";
  }
  return value ?? "";
}

function hasPiToken(raw) { return /(?:\\pi|π|Π|pi)/i.test(String(raw ?? "")); }
// This input's public contract returns the coefficient of pi.
export function parseRootFractionPiInput(raw) {
 const text = String(raw ?? "").split("=").pop();
 const value = numericMathExpression(text);
 return value === null ? null : hasPiToken(text) ? value / Math.PI : value;
}
function coefficientRawForPreview(raw) { return String(raw ?? ""); }

export function attachPiToCoefficientLatex(coefficientLatex) {
  if (!coefficientLatex) return "";

  const frac = String(coefficientLatex).match(/^\\d?frac\{(.+)\}\{(.+)\}$/);
  if (frac) {
    return `\\frac{${putPiBeforeRoot(frac[1])}}{${frac[2]}}`;
  }

  if (coefficientLatex === "1") return "\\pi";
  if (coefficientLatex === "-1") return "-\\pi";
  return putPiBeforeRoot(coefficientLatex);
}

function putPiBeforeRoot(latex) {
  const s = String(latex);
  if (s.includes("\\sqrt")) {
    return s.replace("\\sqrt", "\\pi\\sqrt");
  }
  return `${s}\\pi`;
}

export function formatRootFractionPiPreview(raw, label = "x") {
  label = normalizePreviewLabel(label);
 const valueLatex = previewMathExpression(String(raw ?? "").split("=").pop());
 return valueLatex ? (label ? label + "=" + valueLatex : valueLatex) : null;
}

export default function RootFractionPiInput({
  value,
  onChange,
  disabled,
  label: inputLabel = "x",
  placeholder = "np. 64sqrt(3)/3 pi",
  previewPrefix = "Podgląd:",
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
}) {
  const label = normalizePreviewLabel(inputLabel);
  const [local, setLocal] = useState(pickString(value));
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocal(pickString(value));
  }, [value]);

  const updateLocal = (raw) => {
    setLocal(raw);
    onChange?.({raw, parsed: parseRootFractionPiInput(raw), coefficientRaw: coefficientRawForPreview(raw), hasPi: hasPiToken(raw)});
  };

  const setCaret = (pos) => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? 0;
    const next = local.slice(0, s) + text + local.slice(e);
    updateLocal(next);
    setTimeout(() => setCaret(s + String(text).length), 0);
  };

  const previewLatex = formatRootFractionPiPreview(local, label);

  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };

  const inputStyle = {
    width: `calc(100% - ${indentPx}px)`,
    minWidth: 0,
    flex: "0 0 auto",
  };

  return (
    <div style={containerStyle}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          compact
        />

        <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
          <input
            ref={inputRef}
            type="text"
            value={local}
            onChange={(e) => updateLocal(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className="input-secondary"
            style={inputStyle}
          />
          <button
            type="button"
            onClick={() => insertAtCursor("√")}
            disabled={disabled}
            className="btn-secondary"
            aria-label="Wstaw √"
            title="Wstaw √"
          >
            √
          </button>
          <button
            type="button"
            onClick={() => insertAtCursor("π")}
            disabled={disabled}
            className="btn-secondary"
            aria-label="Wstaw π"
            title="Wstaw π"
          >
            π
          </button>
        </div>
      </div>

      {previewLatex && (
        <div className="font-semibold text-gray-200">
          {previewPrefix ? (
            <span>
              <strong>{previewPrefix}</strong>{" "}
              <InlineMath math={`\\displaystyle ${previewLatex}`} />
            </span>
          ) : (
            <InlineMath math={`\\displaystyle ${previewLatex}`} />
          )}
        </div>
      )}

      {kbdOpen && (
        <div style={{ width: "100%" }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={(type, payload) => {
              const el = inputRef.current;
              if (!el) return;
              const s = el.selectionStart ?? 0;
              const e = el.selectionEnd ?? 0;

              if (type === "insert") {
                insertAtCursor(String(payload ?? ""));
                return;
              }

              if (type === "backspace") {
                if (s !== e) {
                  const next = local.slice(0, s) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s), 0);
                } else if (s > 0) {
                  const next = local.slice(0, s - 1) + local.slice(e);
                  updateLocal(next);
                  setTimeout(() => setCaret(s - 1), 0);
                }
              }
            }}
            compact
          />
        </div>
      )}
    </div>
  );
}
